import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '../styles/AdminDashboard.css';
import AdminMembers from './AdminMembers';
import { AdminPartnersContent } from './AdminPartners';
import AdminAccounts from './AdminAccounts';
import AdminSettings from './AdminSettings';
import { AdminProductsContent } from './AdminProducts';
import { AdminReservationsContent } from './AdminReservations';
import { AdminActivitesContent } from './AdminActivites';
import AdminProjects from './AdminProjects';

function AdminDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n } = useTranslation();
  const [actionLogs, setActionLogs] = useState([]); // eslint-disable-line no-unused-vars
  const [partnerRequests, setPartnerRequests] = useState([]);
  const [loading, setLoading] = useState(true); // eslint-disable-line no-unused-vars
  const [activeSection, setActiveSection] = useState(
    location.state?.section || 'dashboard'
  );
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  
  // Nouvelles statistiques
  const [stats, setStats] = useState({
    membres: 0,
    projets: 0,
    reservations: 0,
    activites: 0,
    produits: 0,
    partenaires: 0
  });

  const isSuperAdmin = localStorage.getItem('is_superadmin') === 'true';
  const [username, setUsername] = useState(localStorage.getItem('username') || '');

  useEffect(() => {
    // Forcer le français dans l'espace admin — sauvegarder la langue user d'abord
    const userLang = localStorage.getItem('i18nextLng') || 'fr';
    if (userLang !== 'fr') {
      localStorage.setItem('admin_saved_lang', userLang);
    }
    i18n.changeLanguage('fr');
    document.documentElement.setAttribute('lang', 'fr');
    document.body.dir = 'ltr';

    // Restaurer la langue user quand on quitte l'admin
    return () => {
      const savedLang = localStorage.getItem('admin_saved_lang');
      if (savedLang) {
        i18n.changeLanguage(savedLang);
        document.documentElement.setAttribute('lang', savedLang);
        document.body.dir = savedLang === 'ar' ? 'rtl' : 'ltr';
        localStorage.removeItem('admin_saved_lang');
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchActionLogs();
    fetchPartnerRequests();
    fetchStats();
    // Synchronise username + permissions depuis l'API au chargement
    const token = localStorage.getItem('access_token');
    fetch(`${process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api'}/me/`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (!data) return;
        if (data.username) {
          localStorage.setItem('username', data.username);
          setUsername(data.username);
        }
        localStorage.setItem('is_superadmin', data.is_superadmin);
        localStorage.setItem('can_manage_partners', data.can_manage_partners);
        localStorage.setItem('can_manage_members', data.can_manage_members);
        localStorage.setItem('can_manage_events', data.can_manage_events);
        localStorage.setItem('can_manage_products', data.can_manage_products);
        localStorage.setItem('can_manage_projects', data.can_manage_projects);
        localStorage.setItem('can_manage_reservations', data.can_manage_reservations);
      })
      .catch(() => { });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPartnerRequests = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api'}/partner-requests/`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setPartnerRequests(data);
      }
    } catch (error) {
      console.error("Error fetching partner requests:", error);
    }
  };

  const fetchActionLogs = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api'}/action-logs/`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setActionLogs(data);
      }
    } catch (error) {
      console.error("Error fetching action logs:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('access_token');
      
      const [productsRes, reservationsRes, membersRes, activitesRes, projectsRes, partnersRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api'}/products/`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api'}/reservations/`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api'}/members/`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api'}/activites/`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api'}/projects/`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api'}/partners/`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
      ]);

      const [products, reservations, members, activites, projects, partners] = await Promise.all([
        productsRes.ok ? productsRes.json() : [],
        reservationsRes.ok ? reservationsRes.json() : [],
        membersRes.ok ? membersRes.json() : [],
        activitesRes.ok ? activitesRes.json() : [],
        projectsRes.ok ? projectsRes.json() : [],
        partnersRes.ok ? partnersRes.json() : [],
      ]);

      setStats({
        produits: products.length,
        reservations: reservations.length,
        membres: members.length,
        activites: activites.length,
        projets: projects.length,
        partenaires: partners.length,
      });
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const handleLogout = () => {
    // Restaurer la langue user avant de déconnecter
    const savedLang = localStorage.getItem('admin_saved_lang');
    localStorage.clear();
    if (savedLang) {
      i18n.changeLanguage(savedLang);
      document.documentElement.setAttribute('lang', savedLang);
      document.body.dir = savedLang === 'ar' ? 'rtl' : 'ltr';
      localStorage.setItem('i18nextLng', savedLang);
    }
    navigate('/admin/login');
  };

  return (
    <div className={`admin-layout ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      {/* ── Sidebar ── */}
      <aside className={`admin-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
        <div className="sidebar-logo-group">
          <div className="sidebar-logo">TIWIZI ADMIN</div>
          <button className="sidebar-toggle" onClick={() => setSidebarCollapsed(!sidebarCollapsed)}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              {sidebarCollapsed ? <polyline points="9 18 15 12 9 6" /> : <polyline points="15 18 9 12 15 6" />}
            </svg>
          </button>
        </div>

        <nav className="sidebar-nav">
          <div
            className={`nav-item${activeSection === 'dashboard' ? ' active' : ''}`}
            onClick={() => setActiveSection('dashboard')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /></svg>
            <span>Tableau de bord</span>
          </div>

          <div
            className={`nav-item${activeSection === 'partenaires' ? ' active' : ''}`}
            onClick={() => setActiveSection('partenaires')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
            <span>Partenaires</span>
          </div>

          <div
            className={`nav-item${activeSection === 'evenements' ? ' active' : ''}`}
            onClick={() => setActiveSection('evenements')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
            </svg>
            <span>Activités</span>
          </div>

          <div
            className={`nav-item${activeSection === 'membres' ? ' active' : ''}`}
            onClick={() => setActiveSection('membres')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /></svg>
            <span>Membres</span>
          </div>

          <div
            className={`nav-item${activeSection === 'reservations' ? ' active' : ''}`}
            onClick={() => setActiveSection('reservations')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
            <span>Réservations</span>
          </div>

          <div
            className={`nav-item${activeSection === 'projets' ? ' active' : ''}`}
            onClick={() => setActiveSection('projets')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>
            <span>Projets</span>
          </div>

          <div
            className={`nav-item${activeSection === 'produits' ? ' active' : ''}`}
            onClick={() => setActiveSection('produits')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" /><line x1="3" y1="6" x2="21" y2="6" /><path d="M16 10a4 4 0 0 1-8 0" /></svg>
            <span>Produits</span>
          </div>

          <div
            className={`nav-item${activeSection === 'parametres' ? ' active' : ''}`}
            onClick={() => setActiveSection('parametres')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" /></svg>
            <span>Paramètres</span>
          </div>

          {isSuperAdmin && (
            <div
              className={`nav-item${activeSection === 'admins' ? ' active' : ''}`}
              onClick={() => setActiveSection('admins')}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
              <span>Administrateurs</span>
            </div>
          )}
        </nav>

        <div className="sidebar-footer">
          <div className="user-profile-card">
            <div className="user-avatar">{username ? username.charAt(0).toUpperCase() : '?'}</div>
            <div className="user-info">
              <span className="user-name">{username}</span>
              <span className="user-role">{isSuperAdmin ? 'SUPERADMIN' : 'ADMINISTRATEUR'}</span>
            </div>
          </div>
          <button onClick={handleLogout} className="logout-btn">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></svg>
            <span>Déconnexion</span>
          </button>
        </div>
      </aside>

      {/* ── Main Content ── */}
      <main className="admin-main">
        <header className="admin-header">
          <div className="header-title-group">
            <h2>
              {activeSection === 'partenaires' ? 'Gestion des Partenaires' :
               activeSection === 'membres' ? 'Gestion des Membres' :
               activeSection === 'reservations' ? 'Gestion des Réservations' :
               activeSection === 'projets' ? 'Gestion des Projets' :
               activeSection === 'admins' ? 'Gestion des Comptes Admins' :
               activeSection === 'evenements' ? 'Gestion des Activités' :
               activeSection === 'parametres' ? 'Paramètres du compte' :
               activeSection === 'produits' ? 'Gestion des Produits' :
               'Tableau de bord'}
            </h2>
            <p>
              {activeSection === 'dashboard' ? 'Gérez votre plateforme en toute simplicité.' :
                'Contrôlez et configurez les paramètres de votre espace.'}
            </p>
          </div>
          <div className="header-status">
            Connecté en tant que <strong>{username}</strong>
            <span className="status-dot"></span>
          </div>
        </header>

        <div className="admin-content">
          {activeSection === 'dashboard' && (
            <>
              {/* Stats Grid */}
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-icon" style={{ backgroundColor: '#fef3c7', color: '#92400e' }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
                      <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
                    </svg>
                  </div>
                  <div className="stat-info">
                    <h3>Produits</h3>
                    <p>{stats.produits}</p>
                    <span className="stat-badge badge-success">Disponibles</span>
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-icon" style={{ backgroundColor: '#dbeafe', color: '#1e40af' }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                      <line x1="16" y1="2" x2="16" y2="6"></line>
                      <line x1="8" y1="2" x2="8" y2="6"></line>
                      <line x1="3" y1="10" x2="21" y2="10"></line>
                    </svg>
                  </div>
                  <div className="stat-info">
                    <h3>Réservations</h3>
                    <p>{stats.reservations}</p>
                    <span className="stat-badge badge-stable">Total</span>
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-icon" style={{ backgroundColor: '#f0fdf4', color: '#166534' }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                      <circle cx="9" cy="7" r="4"></circle>
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                      <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                    </svg>
                  </div>
                  <div className="stat-info">
                    <h3>Membres</h3>
                    <p>{stats.membres}</p>
                    <span className="stat-badge badge-success">Actifs</span>
                  </div>
                </div>
              </div>

              {/* Notifications Section */}
              <section className="data-section">
                <div className="section-header">
                  <div className="section-title-group">
                    <h3>Notifications récentes</h3>
                    <p>Restez informé des dernières activités sur la plateforme.</p>
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {/* Demandes de partenariat */}
                  {partnerRequests.slice(0, 3).map((request) => (
                    <div key={`partner-${request.id}`} style={{ 
                      padding: '20px', 
                      background: '#fffbeb', 
                      borderRadius: '16px',
                      borderLeft: '4px solid #f59e0b',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px'
                    }}>
                      <div style={{ 
                        width: '48px', 
                        height: '48px', 
                        background: '#fef3c7', 
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2.5">
                          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                          <circle cx="9" cy="7" r="4" />
                        </svg>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '700', color: '#1a1a1a', marginBottom: '4px' }}>
                          Nouvelle demande de partenariat
                        </div>
                        <div style={{ fontSize: '0.9rem', color: '#92400e' }}>
                          {request.nom} de {request.organisation}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#999', marginTop: '6px' }}>
                          {request.created_at ? new Date(request.created_at).toLocaleDateString('fr-FR') : ''}
                        </div>
                      </div>
                      <button 
                        onClick={() => setActiveSection('partenaires')}
                        style={{
                          padding: '8px 16px',
                          background: '#f59e0b',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                      >
                        Voir
                      </button>
                    </div>
                  ))}

                  {partnerRequests.length === 0 && (
                    <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
                      Aucune notification pour le moment
                    </div>
                  )}
                </div>
              </section>
            </>
          )}

          {activeSection === 'partenaires' && <AdminPartnersContent />}
          {activeSection === 'membres' && <AdminMembers onStatsChange={fetchStats} />}
          {activeSection === 'reservations' && <AdminReservationsContent onStatsChange={fetchStats} />}
          {activeSection === 'evenements' && <AdminActivitesContent />}
          {activeSection === 'projets' && <AdminProjects />}
          {activeSection === 'produits' && <AdminProductsContent onStatsChange={fetchStats} />}
          {activeSection === 'admins' && <AdminAccounts />}
          {activeSection === 'parametres' && <AdminSettings />}
        </div>
      </main>
    </div>
  );
}

export default AdminDashboard;
